import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Trash2, Plus } from "lucide-react";
import { formatBRL, formatNumber } from "./projectSchema";

/** Shared field wrappers */
export function Field({ label, children, className = "" }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

export function NumberInput({ value, onChange, step = "any", ...rest }) {
  return (
    <Input
      type="number"
      step={step}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? 0 : parseFloat(e.target.value))}
      {...rest}
    />
  );
}

function setAt(obj, path, value) {
  const copy = { ...obj };
  const parts = path.split(".");
  let cur = copy;
  for (let i = 0; i < parts.length - 1; i++) {
    cur[parts[i]] = Array.isArray(cur[parts[i]]) ? [...cur[parts[i]]] : { ...(cur[parts[i]] || {}) };
    cur = cur[parts[i]];
  }
  cur[parts[parts.length - 1]] = value;
  return copy;
}

// -------- Step 1: Dados Básicos --------
export function StepBasic({ data, update }) {
  const d = data.basic_data || {};
  const a = data.applicant || {};
  const s = data.spouse || {};
  const t = data.ates || {};
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        <Field label="Agência do Beneficiário">
          <Input value={d.agencia_beneficiario || ""} onChange={(e) => update(setAt(data, "basic_data.agencia_beneficiario", e.target.value))} data-testid="basic-agencia" />
        </Field>
        <Field label="Conta Corrente">
          <Input value={d.conta_corrente || ""} onChange={(e) => update(setAt(data, "basic_data.conta_corrente", e.target.value))} data-testid="basic-conta" />
        </Field>
        <Field label="Data">
          <Input type="date" value={d.data || ""} onChange={(e) => update(setAt(data, "basic_data.data", e.target.value))} data-testid="basic-data" />
        </Field>
        <Field label="Agência do Assentamento">
          <Input value={d.agencia_assentamento || ""} onChange={(e) => update(setAt(data, "basic_data.agencia_assentamento", e.target.value))} />
        </Field>
        <Field label="Assentamento">
          <Input value={d.assentamento || ""} onChange={(e) => update(setAt(data, "basic_data.assentamento", e.target.value))} data-testid="basic-assentamento" />
        </Field>
        <Field label="Município">
          <Input value={d.municipio || ""} onChange={(e) => update(setAt(data, "basic_data.municipio", e.target.value))} data-testid="basic-municipio" />
        </Field>
      </div>

      <div>
        <h3 className="font-display font-semibold text-lg mb-3">Proponente (Titular)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <Field label="Nome completo" className="lg:col-span-2">
            <Input value={a.nome || ""} onChange={(e) => update(setAt(data, "applicant.nome", e.target.value))} data-testid="applicant-nome" />
          </Field>
          <Field label="CPF">
            <Input value={a.cpf || ""} onChange={(e) => update(setAt(data, "applicant.cpf", e.target.value))} data-testid="applicant-cpf" />
          </Field>
          <Field label="Estado Civil">
            <Input value={a.estado_civil || ""} onChange={(e) => update(setAt(data, "applicant.estado_civil", e.target.value))} />
          </Field>
          <Field label="Nº SIPRA">
            <Input value={a.sipra || ""} onChange={(e) => update(setAt(data, "applicant.sipra", e.target.value))} />
          </Field>
        </div>
      </div>

      <div>
        <h3 className="font-display font-semibold text-lg mb-3">Cônjuge <span className="text-xs font-normal text-muted-foreground">(se houver)</span></h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <Field label="Nome completo" className="lg:col-span-2">
            <Input value={s.nome || ""} onChange={(e) => update(setAt(data, "spouse.nome", e.target.value))} data-testid="spouse-nome" />
          </Field>
          <Field label="CPF">
            <Input value={s.cpf || ""} onChange={(e) => update(setAt(data, "spouse.cpf", e.target.value))} />
          </Field>
          <Field label="Estado Civil">
            <Input value={s.estado_civil || ""} onChange={(e) => update(setAt(data, "spouse.estado_civil", e.target.value))} />
          </Field>
          <Field label="Nº SIPRA">
            <Input value={s.sipra || ""} onChange={(e) => update(setAt(data, "spouse.sipra", e.target.value))} />
          </Field>
        </div>
      </div>

      <div>
        <h3 className="font-display font-semibold text-lg mb-3">Responsável Técnico (ATES)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <Field label="Nome" className="lg:col-span-2">
            <Input value={t.nome || ""} onChange={(e) => update(setAt(data, "ates.nome", e.target.value))} data-testid="ates-nome" />
          </Field>
          <Field label="CREA / CRMV">
            <Input value={t.crea_crmv || ""} onChange={(e) => update(setAt(data, "ates.crea_crmv", e.target.value))} />
          </Field>
        </div>
      </div>
    </div>
  );
}

// -------- Step 2: Crédito --------
export function StepCredit({ data, update }) {
  const c = data.credit || { items: [] };
  const addRow = () => update({ ...data, credit: { ...c, items: [...(c.items || []), { quantidade: 1, especificacao: "", valor_unitario: 0 }] } });
  const removeRow = (i) => update({ ...data, credit: { ...c, items: c.items.filter((_, idx) => idx !== i) } });
  const updateRow = (i, key, val) => {
    const items = [...c.items];
    items[i] = { ...items[i], [key]: val };
    update({ ...data, credit: { ...c, items } });
  };
  const total = (c.items || []).reduce((a, it) => a + (Number(it.quantidade) || 0) * (Number(it.valor_unitario) || 0), 0);

  return (
    <div className="space-y-6">
      <Field label="Contratação">
        <Select value={c.contratacao || "1ª"} onValueChange={(v) => update({ ...data, credit: { ...c, contratacao: v } })}>
          <SelectTrigger className="w-56" data-testid="credit-contratacao"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1ª">1ª Contratação</SelectItem>
            <SelectItem value="2ª">2ª Contratação</SelectItem>
            <SelectItem value="3ª">3ª Contratação</SelectItem>
            <SelectItem value="4ª">4ª Contratação</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-semibold text-lg">Itens de Investimento</h3>
          <Button variant="outline" size="sm" onClick={addRow} data-testid="add-credit-item"><Plus className="h-4 w-4 mr-1" /> Adicionar item</Button>
        </div>
        <div className="border rounded-md overflow-hidden">
          <Table className="pronaf-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-28">Quantidade</TableHead>
                <TableHead>Especificação</TableHead>
                <TableHead className="w-40">Valor Unitário</TableHead>
                <TableHead className="w-40">Total</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(c.items || []).map((it, i) => (
                <TableRow key={i}>
                  <TableCell><NumberInput value={it.quantidade} onChange={(v) => updateRow(i, "quantidade", v)} data-testid={`credit-qty-${i}`} /></TableCell>
                  <TableCell><Input value={it.especificacao || ""} onChange={(e) => updateRow(i, "especificacao", e.target.value)} data-testid={`credit-spec-${i}`} /></TableCell>
                  <TableCell><NumberInput value={it.valor_unitario} onChange={(v) => updateRow(i, "valor_unitario", v)} data-testid={`credit-unit-${i}`} /></TableCell>
                  <TableCell className="font-mono text-sm">{formatBRL((Number(it.quantidade) || 0) * (Number(it.valor_unitario) || 0))}</TableCell>
                  <TableCell><Button variant="ghost" size="icon" onClick={() => removeRow(i)} data-testid={`remove-credit-${i}`}><Trash2 className="h-4 w-4 text-destructive" /></Button></TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-secondary/50 font-semibold">
                <TableCell colSpan={3} className="text-right">Total</TableCell>
                <TableCell className="font-mono">{formatBRL(total)}</TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

// -------- Step 3: Financing --------
export function StepFinancing({ data, update }) {
  const f = data.financing || {};
  const set = (k, v) => update({ ...data, financing: { ...f, [k]: v } });
  const totalItens = Number(data.credit?.total_itens || (data.credit?.items || []).reduce((a, i) => a + (Number(i.quantidade) || 0) * (Number(i.valor_unitario) || 0), 0));
  const remunAter = totalItens * Number(f.remun_ater_pct || 0);
  const totalOrc = totalItens + remunAter;
  const totalFin = totalOrc - Number(f.recursos_proprios || 0);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        <Field label="Recursos Próprios (R$)">
          <NumberInput value={f.recursos_proprios} onChange={(v) => set("recursos_proprios", v)} data-testid="fin-recursos-proprios" />
        </Field>
        <Field label="Prazo (anos)">
          <NumberInput value={f.prazo_anos} step="1" onChange={(v) => set("prazo_anos", Math.max(1, Math.round(v)))} data-testid="fin-prazo" />
        </Field>
        <Field label="Taxa de Juros (% a.a., ex: 0.005 = 0,5%)">
          <NumberInput value={f.juros_aa} onChange={(v) => set("juros_aa", v)} data-testid="fin-juros" />
        </Field>
        <Field label="Carência (meses)">
          <NumberInput value={f.carencia_meses} step="1" onChange={(v) => set("carencia_meses", Math.round(v))} data-testid="fin-carencia" />
        </Field>
        <Field label="Rebate (ex: 0.43 = 43%)">
          <NumberInput value={f.rebate} onChange={(v) => set("rebate", v)} data-testid="fin-rebate" />
        </Field>
        <Field label="% Remuneração ATER">
          <NumberInput value={f.remun_ater_pct} onChange={(v) => set("remun_ater_pct", v)} />
        </Field>
        <Field label="ATER financiada?">
          <Select value={f.ater_financiada || "sim"} onValueChange={(v) => set("ater_financiada", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="sim">Sim</SelectItem>
              <SelectItem value="nao">Não</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Tipo de parcelas">
          <Select value={f.parcelas_tipo || "Semestral"} onValueChange={(v) => set("parcelas_tipo", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Semestral">Semestral</SelectItem>
              <SelectItem value="Anual">Anual</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Bens remanescentes">
          <Input value={f.bens_remanescentes || ""} onChange={(e) => set("bens_remanescentes", e.target.value)} />
        </Field>
      </div>

      <div className="bg-secondary/50 border rounded-md p-5 grid grid-cols-2 md:grid-cols-4 gap-5">
        <div>
          <div className="text-xs uppercase text-muted-foreground">Total dos itens</div>
          <div className="font-mono font-semibold text-lg">{formatBRL(totalItens)}</div>
        </div>
        <div>
          <div className="text-xs uppercase text-muted-foreground">Remuneração ATER</div>
          <div className="font-mono font-semibold text-lg">{formatBRL(remunAter)}</div>
        </div>
        <div>
          <div className="text-xs uppercase text-muted-foreground">Orçamento + ATER</div>
          <div className="font-mono font-semibold text-lg">{formatBRL(totalOrc)}</div>
        </div>
        <div>
          <div className="text-xs uppercase text-muted-foreground">Total do Financiamento</div>
          <div className="font-mono font-semibold text-lg text-primary">{formatBRL(totalFin)}</div>
        </div>
      </div>
    </div>
  );
}

// -------- Step 4: Schedule --------
export function StepSchedule({ data }) {
  const s = data.reimbursement_schedule || [];
  if (s.length === 0) {
    return (
      <div className="text-sm text-muted-foreground p-8 text-center border border-dashed rounded-md">
        O cronograma é calculado automaticamente. Clique em <b>"Recalcular"</b> após preencher os itens de crédito e condições do financiamento.
      </div>
    );
  }
  return (
    <div className="border rounded-md overflow-hidden overflow-x-auto">
      <Table className="pronaf-table">
        <TableHeader>
          <TableRow>
            <TableHead>Parcela</TableHead>
            <TableHead>Saldo Inicial</TableHead>
            <TableHead>Juros</TableHead>
            <TableHead>Amortização</TableHead>
            <TableHead>Rebate</TableHead>
            <TableHead>Parcela</TableHead>
            <TableHead>Paga em Dia</TableHead>
            <TableHead>Saldo Final</TableHead>
            <TableHead>Fase</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {s.map((r) => (
            <TableRow key={r.parcela}>
              <TableCell>{r.parcela}</TableCell>
              <TableCell className="font-mono">{formatBRL(r.saldo_inicial)}</TableCell>
              <TableCell className="font-mono">{formatBRL(r.juros)}</TableCell>
              <TableCell className="font-mono">{formatBRL(r.amortizacao)}</TableCell>
              <TableCell className="font-mono">{formatBRL(r.rebate)}</TableCell>
              <TableCell className="font-mono">{formatBRL(r.parcela_valor)}</TableCell>
              <TableCell className="font-mono font-semibold">{formatBRL(r.parcela_paga_em_dia)}</TableCell>
              <TableCell className="font-mono">{formatBRL(r.saldo_final)}</TableCell>
              <TableCell className="text-xs">{r.em_carencia ? "Carência" : "Amortização"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// -------- Step 5: Plot --------
export function StepPlot({ data, update }) {
  const p = data.plot || {};
  const set = (k, v) => update({ ...data, plot: { ...p, [k]: v } });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      <Field label="Matrícula"><Input value={p.matricula || ""} onChange={(e) => set("matricula", e.target.value)} data-testid="plot-matricula" /></Field>
      <Field label="Nº do Lote"><Input value={p.numero_lote || ""} onChange={(e) => set("numero_lote", e.target.value)} /></Field>
      <Field label="Município"><Input value={p.municipio || ""} onChange={(e) => set("municipio", e.target.value)} /></Field>
      <Field label="Distrito"><Input value={p.distrito || ""} onChange={(e) => set("distrito", e.target.value)} /></Field>
      <Field label="Área Total (ha)"><NumberInput value={p.area_total} onChange={(v) => set("area_total", v)} data-testid="plot-area-total" /></Field>
      <Field label="Área Utilizada (ha)"><NumberInput value={p.area_utilizada} onChange={(v) => set("area_utilizada", v)} /></Field>
      <Field label="Lavouras (ha)"><NumberInput value={p.area_lavouras} onChange={(v) => set("area_lavouras", v)} /></Field>
      <Field label="Pastagem (ha)"><NumberInput value={p.area_pastagem} onChange={(v) => set("area_pastagem", v)} /></Field>
      <Field label="Reflorestamento (ha)"><NumberInput value={p.area_reflorestamento} onChange={(v) => set("area_reflorestamento", v)} /></Field>
      <Field label="Localização" className="md:col-span-2 lg:col-span-3">
        <Textarea value={p.localizacao || ""} onChange={(e) => set("localizacao", e.target.value)} />
      </Field>
      <Field label="Roteiro" className="md:col-span-2 lg:col-span-3">
        <Textarea value={p.roteiro || ""} onChange={(e) => set("roteiro", e.target.value)} />
      </Field>
    </div>
  );
}

// -------- Generic Table component for infra/production/non-agri/laudo --------
function EditableTable({ rows, columns, onChange, testPrefix }) {
  const set = (i, key, val) => {
    const next = [...rows];
    next[i] = { ...next[i], [key]: val };
    onChange(next);
  };
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));
  return (
    <div className="border rounded-md overflow-hidden overflow-x-auto">
      <Table className="pronaf-table">
        <TableHeader>
          <TableRow>
            {columns.map((c) => (<TableHead key={c.key} className={c.width || ""}>{c.label}</TableHead>))}
            <TableHead className="w-10"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={columns.length + 1} className="text-center text-sm text-muted-foreground py-6">Nenhum registro. Clique em "Adicionar".</TableCell></TableRow>
          )}
          {rows.map((row, i) => (
            <TableRow key={i}>
              {columns.map((c) => (
                <TableCell key={c.key}>
                  {c.type === "number" ? (
                    <NumberInput value={row[c.key]} onChange={(v) => set(i, c.key, v)} data-testid={`${testPrefix}-${c.key}-${i}`} />
                  ) : (
                    <Input value={row[c.key] || ""} onChange={(e) => set(i, c.key, e.target.value)} data-testid={`${testPrefix}-${c.key}-${i}`} />
                  )}
                </TableCell>
              ))}
              <TableCell><Button variant="ghost" size="icon" onClick={() => remove(i)} data-testid={`${testPrefix}-remove-${i}`}><Trash2 className="h-4 w-4 text-destructive" /></Button></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// -------- Step 6: Infra --------
export function StepInfra({ data, update }) {
  const infra = data.infrastructure || { benfeitorias: [], maquinas: [], rebanho: [] };
  const setInfra = (key, rows) => update({ ...data, infrastructure: { ...infra, [key]: rows } });
  const benfCols = [
    { key: "discriminacao", label: "Discriminação" },
    { key: "quantidade", label: "Qtd", type: "number", width: "w-20" },
    { key: "area_m2", label: "Área (m²)", type: "number", width: "w-24" },
    { key: "material", label: "Material" },
    { key: "idade", label: "Idade", type: "number", width: "w-20" },
    { key: "conservacao", label: "Conservação" },
    { key: "valor", label: "Valor (R$)", type: "number", width: "w-32" },
  ];
  const maqCols = [
    { key: "discriminacao", label: "Discriminação" },
    { key: "quantidade", label: "Qtd", type: "number", width: "w-20" },
    { key: "marca", label: "Marca" },
    { key: "modelo", label: "Modelo" },
    { key: "potencia", label: "Potência" },
    { key: "ano", label: "Ano" },
    { key: "valor", label: "Valor (R$)", type: "number", width: "w-32" },
  ];
  const rebCols = [
    { key: "especie", label: "Espécie" },
    { key: "raca", label: "Raça" },
    { key: "caracterizacao", label: "Caracterização" },
    { key: "quantidade", label: "Qtd", type: "number", width: "w-20" },
    { key: "valor_unit", label: "Valor Unit.", type: "number", width: "w-32" },
    { key: "marca", label: "Marca" },
    { key: "local_marca", label: "Local Marca" },
  ];

  const totalBens =
    (infra.benfeitorias || []).reduce((a, b) => a + Number(b.valor || 0), 0) +
    (infra.maquinas || []).reduce((a, b) => a + Number(b.valor || 0), 0) +
    (infra.rebanho || []).reduce((a, r) => a + (Number(r.quantidade || 0) * Number(r.valor_unit || 0)), 0);

  return (
    <div className="space-y-8">
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-semibold text-lg">6.1. Benfeitorias</h3>
          <Button size="sm" variant="outline" onClick={() => setInfra("benfeitorias", [...(infra.benfeitorias || []), {}])} data-testid="add-benfeitoria"><Plus className="h-4 w-4 mr-1" /> Adicionar</Button>
        </div>
        <EditableTable rows={infra.benfeitorias || []} columns={benfCols} onChange={(rows) => setInfra("benfeitorias", rows)} testPrefix="benf" />
      </section>
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-semibold text-lg">6.2. Máquinas, Veículos e Equipamentos</h3>
          <Button size="sm" variant="outline" onClick={() => setInfra("maquinas", [...(infra.maquinas || []), {}])} data-testid="add-maquina"><Plus className="h-4 w-4 mr-1" /> Adicionar</Button>
        </div>
        <EditableTable rows={infra.maquinas || []} columns={maqCols} onChange={(rows) => setInfra("maquinas", rows)} testPrefix="maq" />
      </section>
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-semibold text-lg">6.3. Rebanho</h3>
          <Button size="sm" variant="outline" onClick={() => setInfra("rebanho", [...(infra.rebanho || []), {}])} data-testid="add-rebanho"><Plus className="h-4 w-4 mr-1" /> Adicionar</Button>
        </div>
        <EditableTable rows={infra.rebanho || []} columns={rebCols} onChange={(rows) => setInfra("rebanho", rows)} testPrefix="reb" />
      </section>

      <div className="bg-secondary/50 border rounded-md p-5 flex justify-between items-center">
        <div className="text-xs uppercase text-muted-foreground">7. Valor total dos bens presentes no lote</div>
        <div className="font-mono font-bold text-xl text-primary">{formatBRL(totalBens)}</div>
      </div>
    </div>
  );
}

// -------- Step 7: Production --------
export function StepProduction({ data, update }) {
  const prod = data.production || { lavouras: [], pecuaria: [] };
  const setProd = (key, rows) => update({ ...data, production: { ...prod, [key]: rows } });
  const lavCols = [
    { key: "produto", label: "Produto" },
    { key: "unidade", label: "Unid.", width: "w-20" },
    { key: "prod_ano1", label: "Prod. A1", type: "number" },
    { key: "area_ano1", label: "Área A1", type: "number" },
    { key: "prod_ano2", label: "Prod. A2", type: "number" },
    { key: "area_ano2", label: "Área A2", type: "number" },
    { key: "prod_ano3", label: "Prod. A3", type: "number" },
    { key: "area_ano3", label: "Área A3", type: "number" },
    { key: "prod_seguintes", label: "Prod. A+", type: "number" },
    { key: "area_seguintes", label: "Área A+", type: "number" },
  ];
  const pecCols = [
    { key: "produto", label: "Produto" },
    { key: "unidade", label: "Unid." },
    { key: "qtd_ano1", label: "Qtd. A1", type: "number" },
    { key: "area_ano1", label: "Área A1", type: "number" },
    { key: "qtd_ano2", label: "Qtd. A2", type: "number" },
    { key: "area_ano2", label: "Área A2", type: "number" },
    { key: "qtd_ano3", label: "Qtd. A3", type: "number" },
    { key: "area_ano3", label: "Área A3", type: "number" },
    { key: "qtd_seguintes", label: "Qtd. A+", type: "number" },
    { key: "area_seguintes", label: "Área A+", type: "number" },
  ];
  return (
    <div className="space-y-8">
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-semibold text-lg">8.1. Lavouras</h3>
          <Button size="sm" variant="outline" onClick={() => setProd("lavouras", [...(prod.lavouras || []), { produto: "", unidade: "kg" }])} data-testid="add-lavoura"><Plus className="h-4 w-4 mr-1" /> Adicionar</Button>
        </div>
        <EditableTable rows={prod.lavouras || []} columns={lavCols} onChange={(rows) => setProd("lavouras", rows)} testPrefix="lav" />
      </section>
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-semibold text-lg">8.2. Pecuária</h3>
          <Button size="sm" variant="outline" onClick={() => setProd("pecuaria", [...(prod.pecuaria || []), { produto: "", unidade: "" }])} data-testid="add-pecuaria"><Plus className="h-4 w-4 mr-1" /> Adicionar</Button>
        </div>
        <EditableTable rows={prod.pecuaria || []} columns={pecCols} onChange={(rows) => setProd("pecuaria", rows)} testPrefix="pec" />
      </section>
    </div>
  );
}

// -------- Step 8: Non-agri --------
export function StepNonAgri({ data, update }) {
  const rows = data.non_agri_revenue || [];
  const cols = [
    { key: "atividade", label: "Atividade" },
    { key: "local", label: "Local" },
    { key: "membros", label: "Membros da família" },
    { key: "renda_anual", label: "Renda Anual (R$)", type: "number" },
  ];
  const total = rows.reduce((a, r) => a + Number(r.renda_anual || 0), 0);
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-semibold text-lg">9. Receitas de Atividades Não Agropecuárias</h3>
        <Button size="sm" variant="outline" onClick={() => update({ ...data, non_agri_revenue: [...rows, {}] })} data-testid="add-non-agri"><Plus className="h-4 w-4 mr-1" /> Adicionar</Button>
      </div>
      <EditableTable rows={rows} columns={cols} onChange={(r) => update({ ...data, non_agri_revenue: r })} testPrefix="nonagri" />
      <div className="flex justify-end bg-secondary/50 border rounded-md p-4">
        <div className="text-right">
          <div className="text-xs uppercase text-muted-foreground">Total</div>
          <div className="font-mono font-bold text-lg text-primary">{formatBRL(total)}</div>
        </div>
      </div>
    </div>
  );
}

// -------- Step 9: Family --------
export function StepFamily({ data, update }) {
  return (
    <div className="max-w-md">
      <Field label="Manutenção Anual da Família (R$)">
        <NumberInput value={data.family_maintenance_annual} onChange={(v) => update({ ...data, family_maintenance_annual: v })} data-testid="family-maintenance" />
      </Field>
      <p className="text-xs text-muted-foreground mt-3">Valor anual destinado à subsistência da família.</p>
    </div>
  );
}

// -------- Step 10: Laudo --------
export function StepLaudo({ data, update }) {
  const l = data.laudo || { items: [], descricao_pormenorizada: "" };
  const cols = [
    { key: "quantidade", label: "Qtd", type: "number", width: "w-20" },
    { key: "especificacao", label: "Especificação" },
    { key: "valor_unitario", label: "Valor Unit.", type: "number" },
    { key: "valor_aplicado", label: "Valor Aplicado", type: "number" },
    { key: "valor_nao_aplicado", label: "Não Aplicado", type: "number" },
  ];
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-semibold text-lg">11. Comprovação de aplicação da parcela anterior</h3>
        <Button size="sm" variant="outline" onClick={() => update({ ...data, laudo: { ...l, items: [...(l.items || []), {}] } })} data-testid="add-laudo"><Plus className="h-4 w-4 mr-1" /> Adicionar</Button>
      </div>
      <EditableTable rows={l.items || []} columns={cols} onChange={(rows) => update({ ...data, laudo: { ...l, items: rows } })} testPrefix="laudo" />
      <Field label="Situação da aplicação do crédito (descrição pormenorizada)">
        <Textarea rows={5} value={l.descricao_pormenorizada || ""} onChange={(e) => update({ ...data, laudo: { ...l, descricao_pormenorizada: e.target.value } })} data-testid="laudo-descricao" />
      </Field>
    </div>
  );
}

// -------- Step 11: Recs --------
export function StepRecs({ data, update }) {
  const r = data.recommendations || {};
  const set = (k, v) => update({ ...data, recommendations: { ...r, [k]: v } });
  const areas = [
    { k: "recomendacoes_capag", label: "Recomendações para liberação/retenção com CAPAG atualizada" },
    { k: "comercializacao", label: "Comercialização da Produção" },
    { k: "recomendacoes_gerais", label: "Recomendações Técnicas Gerais" },
    { k: "orientacao_ambiental", label: "Orientação Sobre Legislação Ambiental" },
    { k: "compromisso_tecnico", label: "Compromisso Técnico" },
    { k: "observacoes", label: "Observações" },
  ];
  return (
    <div className="space-y-5">
      {areas.map((a) => (
        <Field key={a.k} label={a.label}>
          <Textarea rows={4} value={r[a.k] || ""} onChange={(e) => set(a.k, e.target.value)} data-testid={`rec-${a.k}`} />
        </Field>
      ))}
    </div>
  );
}

// -------- Step 12: Cash Flow --------
export function StepCashFlow({ data, update }) {
  const cf = data.cash_flow || {};
  const calc = data.cash_flow_calc?.rows || [];
  const fields = [
    { k: "receitas_agricolas_anuais", label: "Receitas Agrícolas" },
    { k: "receitas_pecuarias_anuais", label: "Receitas Pecuárias" },
    { k: "receitas_outras_anuais", label: "Outras Receitas" },
    { k: "despesas_agricolas_anuais", label: "Despesas Agrícolas" },
    { k: "despesas_pecuarias_anuais", label: "Despesas Pecuárias" },
    { k: "despesas_outras_anuais", label: "Outras Despesas" },
    { k: "dividas_anuais", label: "Dívidas" },
    { k: "custos_fixos_anuais", label: "Custos Fixos" },
    { k: "manutencao_familiar_anuais", label: "Manutenção Familiar" },
  ];
  const setRow = (k, i, v) => {
    const arr = [...(cf[k] || Array(10).fill(0))];
    arr[i] = v;
    update({ ...data, cash_flow: { ...cf, [k]: arr } });
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Informe valores anuais (Ano 1 a Ano 10). A capacidade de pagamento (CAPAG), o saldo do
        exercício e o saldo acumulado são calculados automaticamente.
      </p>
      <div className="border rounded-md overflow-hidden overflow-x-auto">
        <Table className="pronaf-table">
          <TableHeader>
            <TableRow>
              <TableHead className="sticky left-0 bg-card">Categoria</TableHead>
              {Array.from({ length: 10 }).map((_, i) => (<TableHead key={i} className="w-28">Ano {i + 1}</TableHead>))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {fields.map((f) => (
              <TableRow key={f.k}>
                <TableCell className="sticky left-0 bg-card font-medium">{f.label}</TableCell>
                {Array.from({ length: 10 }).map((_, i) => (
                  <TableCell key={i}>
                    <NumberInput value={(cf[f.k] || [])[i]} onChange={(v) => setRow(f.k, i, v)} data-testid={`cf-${f.k}-${i}`} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {calc.length > 0 && (
        <div>
          <h3 className="font-display font-semibold text-lg mb-2">Capacidade de Pagamento (CAPAG) - Cálculo</h3>
          <div className="border rounded-md overflow-hidden overflow-x-auto">
            <Table className="pronaf-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Ano</TableHead>
                  <TableHead>Receitas</TableHead>
                  <TableHead>Despesas</TableHead>
                  <TableHead>MDT</TableHead>
                  <TableHead>Compromisso Fin.</TableHead>
                  <TableHead>MD %</TableHead>
                  <TableHead>Saldo Exerc.</TableHead>
                  <TableHead>Saldo Acum.</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {calc.map((r) => (
                  <TableRow key={r.ano}>
                    <TableCell>Ano {r.ano}</TableCell>
                    <TableCell className="font-mono">{formatBRL(r.total_receitas)}</TableCell>
                    <TableCell className="font-mono">{formatBRL(r.total_despesas)}</TableCell>
                    <TableCell className="font-mono">{formatBRL(r.mdt)}</TableCell>
                    <TableCell className="font-mono">{formatBRL(r.compromisso)}</TableCell>
                    <TableCell className="font-mono">{formatNumber(r.md_pct)}%</TableCell>
                    <TableCell className={`font-mono ${r.saldo_exercicio < 0 ? "text-destructive" : ""}`}>{formatBRL(r.saldo_exercicio)}</TableCell>
                    <TableCell className={`font-mono ${r.saldo_acumulado < 0 ? "text-destructive" : ""}`}>{formatBRL(r.saldo_acumulado)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
